import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BrandWordmark } from "@/components/Brand";
import {
  MeOut,
  MyApiKey,
  MyFeedbackEntry,
  MySession,
  MyStats,
  MySupport,
  api,
} from "@/api/client";
import { useAuthStore } from "@/stores/auth";

const SUPPORT_MAX_WORDS = 200;

export default function SettingsPage() {
  const { email, displayName, isAdmin, logout, setDisplayName } = useAuthStore();
  const [me, setMe] = useState<MeOut | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then((r) => {
        setMe(r);
        // Keep header name in sync if backend has a fresher value than the
        // one cached in local-storage at last login.
        if (r.display_name !== displayName) setDisplayName(r.display_name);
      })
      .catch((e) => setErr((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen flex-col bg-bg text-ink">
      <nav className="flex items-center border-b border-bg-border bg-bg-elevated px-5 py-3 text-white">
        <Link to="/chat" className="mr-8">
          <BrandWordmark />
        </Link>
        <div className="text-sm font-semibold">Settings</div>
        <div className="ml-auto flex items-center gap-4 text-xs text-ink-muted">
          <Link to="/chat" className="hover:text-white">
            ← Back to chat
          </Link>
          {isAdmin && (
            <Link to="/admin" className="hover:text-white">
              Admin
            </Link>
          )}
          <span>{displayName || email}</span>
          <button
            onClick={logout}
            className="rounded-md border border-bg-border px-3 py-1 hover:bg-bg-hover hover:text-white"
          >
            Log out
          </button>
        </div>
      </nav>

      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-3xl space-y-6 p-6">
          {err && <ErrorBox msg={err} />}

          <AccountCard
            me={me}
            onUpdated={(m) => {
              setMe(m);
              setDisplayName(m.display_name);
            }}
          />

          <SecurityCard />

          <ApiKeysCard me={me} />

          <SessionsCard />

          <StatsCard />

          <FeedbackCard />

          <ExportCard />

          <SupportCard />

          <DangerCard />
        </div>
      </div>
    </div>
  );
}

// ───────── Account ─────────

function AccountCard({
  me,
  onUpdated,
}: {
  me: MeOut | null;
  onUpdated: (m: MeOut) => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (me) setName(me.display_name ?? "");
  }, [me]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setOk(false);
    try {
      const r = await api.updateMe({ display_name: name.trim() || null });
      onUpdated(r);
      setOk(true);
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Account">
      <KV k="Email" v={me?.email ?? "…"} />
      <KV
        k="Role"
        v={me ? (me.is_admin ? "Admin" : "User") : "…"}
      />
      <KV k="Member since" v={me ? fmtDate(me.created_at) : "…"} />
      <KV
        k="Last login"
        v={me?.last_login_at ? fmtDateTime(me.last_login_at) : "—"}
      />

      <form onSubmit={save} className="mt-4 border-t border-bg-border pt-4">
        <Label>Display name</Label>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="(use email)"
            className="flex-1 rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white"
          />
          <button
            disabled={saving}
            className="rounded-md border border-white/20 bg-white px-4 text-sm font-semibold text-bg disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <Hint>Shown in the chat sidebar. Leave empty to fall back to email.</Hint>
        {err && <ErrorBox msg={err} />}
        {ok && <SuccessBox msg="Saved." />}
      </form>
    </Card>
  );
}

// ───────── Security: change password + sign out everywhere ─────────

function SecurityCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const logout = useAuthStore((s) => s.logout);

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(null);
    if (newPw.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirm) {
      setErr("Confirmation does not match the new password.");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(oldPw, newPw);
      setOk("Password changed. You'll need to sign in again.");
      setOldPw("");
      setNewPw("");
      setConfirm("");
      // Server bumped password_version → our token is now invalid. Boot
      // the user back to login.
      setTimeout(() => logout(), 1500);
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doSignOutAll() {
    if (
      !window.confirm(
        "Sign out of every device including this one? You'll need to log in again."
      )
    )
      return;
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await api.signOutEverywhere();
      setOk("Signed out everywhere.");
      setTimeout(() => logout(), 800);
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Security">
      <form onSubmit={changePassword} className="space-y-3">
        <div>
          <Label>Current password</Label>
          <PasswordInput value={oldPw} onChange={setOldPw} required />
        </div>
        <div>
          <Label>New password</Label>
          <PasswordInput
            value={newPw}
            onChange={setNewPw}
            required
            minLength={8}
          />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <PasswordInput
            value={confirm}
            onChange={setConfirm}
            required
            minLength={8}
          />
        </div>
        <button
          disabled={busy}
          className="rounded-md border border-white/20 bg-white px-4 py-2 text-sm font-semibold text-bg disabled:opacity-60"
        >
          {busy ? "Saving…" : "Change password"}
        </button>
      </form>

      <div className="mt-6 border-t border-bg-border pt-4">
        <div className="mb-2 text-xs text-ink-muted">
          Bumps your token version on the server, invalidating every JWT
          including the one in this browser.
        </div>
        <button
          onClick={doSignOutAll}
          disabled={busy}
          className="rounded-md border border-bg-border px-4 py-2 text-sm text-white hover:bg-bg-hover disabled:opacity-60"
        >
          Sign out everywhere
        </button>
      </div>

      {err && <ErrorBox msg={err} />}
      {ok && <SuccessBox msg={ok} />}
    </Card>
  );
}


// ───────── API keys for agent clients (MCP) ─────────

const MCP_URL = `${window.location.origin}/mcp`;

function ApiKeysCard({ me }: { me: MeOut | null }) {
  const [keys, setKeys] = useState<MyApiKey[] | null>(null);
  const [name, setName] = useState("");
  const [days, setDays] = useState("90");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The freshly minted secret. Held in memory only, and only until the user
  // dismisses it — the server cannot show it again.
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [client, setClient] = useState<string>("claude");

  const enabled = me?.can_use_mcp ?? false;

  function reload() {
    if (!enabled) return;
    api.myApiKeys().then(setKeys).catch((e) => setErr((e as Error).message));
  }
  // Wait for `me` before loading: the endpoint 403s without the flag and a
  // failed call would surface as a scary error on an ordinary settings page.
  useEffect(reload, [enabled]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Give the key a name so you can tell it apart later.");
      return;
    }
    setBusy(true);
    try {
      const parsed = parseInt(days, 10);
      const r = await api.createApiKey(
        name.trim(),
        Number.isFinite(parsed) && parsed > 0 ? parsed : null
      );
      setFresh(r.key);
      setCopied(false);
      setName("");
      reload();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: MyApiKey) {
    if (
      !window.confirm(
        `Revoke "${k.name}"? Any agent using it stops working immediately.`
      )
    )
      return;
    setErr(null);
    try {
      await api.revokeApiKey(k.id);
      reload();
    } catch (x) {
      setErr((x as Error).message);
    }
  }

  // Connection recipes per client. The endpoint and the header are the
  // only two facts that matter; each client just spells them differently,
  // so the generic row exists for whatever is not listed.
  const recipes: Record<string, { label: string; hint: string; body: string }> = {
    claude: {
      label: "Claude Code",
      hint: "Run this in your terminal:",
      body: `claude mcp add --transport http fintbot ${MCP_URL} --header "Authorization: Bearer ${fresh ?? ""}"`,
    },
    codex: {
      label: "Codex",
      hint: "Export the key, then add this to ~/.codex/config.toml:",
      body: `export FINTBOT_API_KEY="${fresh ?? ""}"

[mcp_servers.fintbot]
url = "${MCP_URL}"
bearer_token_env_var = "FINTBOT_API_KEY"`,
    },
    json: {
      label: "Cursor / JSON",
      hint: "For clients configured with an mcpServers object:",
      body: `{
  "mcpServers": {
    "fintbot": {
      "type": "http",
      "url": "${MCP_URL}",
      "headers": { "Authorization": "Bearer ${fresh ?? ""}" }
    }
  }
}`,
    },
    manual: {
      label: "Anything else",
      hint: "Any MCP client speaking streamable HTTP needs exactly these two:",
      body: `URL:    ${MCP_URL}
Header: Authorization: Bearer ${fresh ?? ""}`,
    },
  };
  const recipe = recipes[client];

  if (me && !enabled) {
    return (
      <Card title="API keys (agent access)">
        <Hint>
          Agent access lets you search the T24 docs from Claude Code, Codex or
          any MCP client. It is off for your account.
        </Hint>
        <div className="rounded-lg border border-bg-border bg-bg p-3 text-xs text-ink-muted">
          Ask an admin to enable it — <span className="text-ink">Admin → Users → MCP</span>.
          It is granted per person rather than by role, because documentation
          excerpts then travel to whichever model provider your client uses.
        </div>
      </Card>
    );
  }

  return (
    <Card title="API keys (agent access)">
      <Hint>
        Connect Claude Code, Codex or any MCP client to search the T24 docs
        from your terminal. A key grants read-only documentation search —
        never chat history, account settings or admin. Treat it like a
        password: it lives in plain text in your client's config file.
      </Hint>

      {fresh && (
        <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
          <div className="mb-2 text-xs font-semibold text-emerald-300">
            Copy this now — it will never be shown again.
          </div>
          <code className="block break-all rounded bg-black/40 p-2 font-mono text-[11px] text-white">
            {fresh}
          </code>

          <div className="mt-4 flex flex-wrap gap-1">
            {Object.entries(recipes).map(([k, r]) => (
              <button
                key={k}
                onClick={() => {
                  setClient(k);
                  setCopied(false);
                }}
                className={`rounded-md px-2.5 py-1 text-xs ${
                  client === k
                    ? "bg-white font-semibold text-bg"
                    : "border border-bg-border text-ink-muted hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="mt-2 text-xs text-ink-muted">{recipe.hint}</div>
          <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all rounded bg-black/40 p-2 font-mono text-[11px] text-ink">
{recipe.body}
          </pre>

          <div className="mt-3 flex gap-2">
            <button
              onClick={() => {
                navigator.clipboard
                  .writeText(recipe.body)
                  .then(() => setCopied(true));
              }}
              className="rounded-md border border-white/20 bg-white px-3 py-1.5 text-xs font-semibold text-bg"
            >
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={() => setFresh(null)}
              className="rounded-md border border-bg-border px-3 py-1.5 text-xs text-white hover:bg-bg-hover"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <form onSubmit={create} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-1">
          <Label>Key name</Label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="work laptop"
            className="w-full rounded-md border border-bg-border bg-bg px-3 py-2 text-sm text-white outline-none focus:border-white/40"
          />
        </div>
        <div className="w-28">
          <Label>Expires (days)</Label>
          <input
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputMode="numeric"
            className="w-full rounded-md border border-bg-border bg-bg px-3 py-2 text-sm text-white outline-none focus:border-white/40"
          />
        </div>
        <button
          disabled={busy}
          className="rounded-md border border-white/20 bg-white px-4 py-2 text-sm font-semibold text-bg disabled:opacity-60"
        >
          {busy ? "Creating…" : "Create key"}
        </button>
      </form>

      {err && <ErrorBox msg={err} />}

      {!keys ? (
        <div className="py-3 text-xs text-ink-dim">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="py-3 text-xs text-ink-dim">No keys yet.</div>
      ) : (
        <table className="mt-4 w-full text-xs">
          <thead>
            <tr className="text-left text-ink-dim">
              <th className="py-1">Name</th>
              <th className="py-1">Key</th>
              <th className="py-1">Last used</th>
              <th className="py-1">Expires</th>
              <th className="py-1"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-bg-border">
                <td className="py-1.5 pr-3 align-top">
                  {k.name}
                  {!k.is_active && (
                    <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-ink-dim">
                      {k.revoked_at ? "revoked" : "expired"}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3 align-top font-mono text-ink-muted">
                  {k.prefix}…
                </td>
                <td className="py-1.5 pr-3 align-top text-ink-muted">
                  {k.last_used_at ? fmtDateTime(k.last_used_at) : "never"}
                </td>
                <td className="py-1.5 pr-3 align-top text-ink-muted">
                  {k.expires_at ? fmtDate(k.expires_at) : "never"}
                </td>
                <td className="py-1.5 align-top">
                  {k.is_active && (
                    <button
                      onClick={() => revoke(k)}
                      className="rounded border border-bg-border px-2 py-0.5 text-[11px] text-white hover:bg-bg-hover"
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ───────── Recent login sessions ─────────

function SessionsCard() {
  const [sessions, setSessions] = useState<MySession[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.mySessions().then(setSessions).catch((e) => setErr((e as Error).message));
  }, []);

  return (
    <Card title="Recent logins">
      <Hint>
        The 10 most recent successful sign-ins on your account. If you see
        anything you don't recognize, change your password and sign out
        everywhere.
      </Hint>
      {err && <ErrorBox msg={err} />}
      {!sessions ? (
        <div className="py-3 text-xs text-ink-dim">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="py-3 text-xs text-ink-dim">No recorded logins yet.</div>
      ) : (
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="text-left text-ink-dim">
              <th className="py-1">When</th>
              <th className="py-1">IP</th>
              <th className="py-1">Device / browser</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} className="border-t border-bg-border">
                <td className="py-1 pr-3 align-top">{fmtDateTime(s.created_at)}</td>
                <td className="py-1 pr-3 align-top">{s.ip ?? "—"}</td>
                <td
                  className="py-1 max-w-md truncate align-top text-ink-muted"
                  title={s.user_agent ?? ""}
                >
                  {shortUA(s.user_agent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ───────── Usage stats ─────────

function StatsCard() {
  const [s, setS] = useState<MyStats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.myStats().then(setS).catch((e) => setErr((e as Error).message));
  }, []);

  return (
    <Card title="Your usage">
      {err && <ErrorBox msg={err} />}
      {!s ? (
        <div className="py-3 text-xs text-ink-dim">Loading…</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Conversations" value={s.conversations} />
          <Stat label="Messages sent" value={s.messages_sent} />
          <Stat label="Days active" value={s.days_active} />
          <Stat label="Feedback given" value={s.feedback_given} />
          {s.first_message_at && (
            <Stat
              label="First message"
              value={fmtDate(s.first_message_at)}
              wide
            />
          )}
          {s.last_message_at && (
            <Stat
              label="Last message"
              value={fmtDate(s.last_message_at)}
              wide
            />
          )}
        </div>
      )}
    </Card>
  );
}

// ───────── Feedback log ─────────

function FeedbackCard() {
  const [items, setItems] = useState<MyFeedbackEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.myFeedback().then(setItems).catch((e) => setErr((e as Error).message));
  }, []);

  return (
    <Card title="My feedback">
      <Hint>
        Every thumbs-up / thumbs-down you've given. Click a row to jump back
        to the conversation.
      </Hint>
      {err && <ErrorBox msg={err} />}
      {!items ? (
        <div className="py-3 text-xs text-ink-dim">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-3 text-xs text-ink-dim">No feedback yet.</div>
      ) : (
        <ul className="divide-y divide-bg-border">
          {items.map((f) => (
            <li key={f.id} className="py-2 text-xs">
              <div className="flex items-center gap-2 text-ink-muted">
                <span
                  className={
                    f.rating > 0
                      ? "text-emerald-400"
                      : "text-red-400"
                  }
                >
                  {f.rating > 0 ? "👍" : "👎"}
                </span>
                <Link
                  to={`/chat?conv=${f.conversation_id}`}
                  className="font-medium text-white hover:underline"
                >
                  {f.conversation_title}
                </Link>
                <span className="text-ink-dim">
                  · {fmtDateTime(f.created_at)}
                </span>
              </div>
              <div className="mt-1 text-ink-muted">{f.message_excerpt}</div>
              {f.comment && (
                <div className="mt-1 italic text-ink-dim">"{f.comment}"</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ───────── Export ─────────

function ExportCard() {
  const token = useAuthStore((s) => s.token);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function download() {
    setBusy(true);
    setErr(null);
    try {
      const resp = await fetch(api.myExportUrl(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fintbot-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Export my data">
      <Hint>
        Downloads everything tied to your account — conversations, messages
        and feedback — as a single JSON file.
      </Hint>
      <button
        onClick={download}
        disabled={busy}
        className="rounded-md border border-white/20 bg-white px-4 py-2 text-sm font-semibold text-bg disabled:opacity-60"
      >
        {busy ? "Preparing…" : "Download JSON"}
      </button>
      {err && <ErrorBox msg={err} />}
    </Card>
  );
}

// ───────── Support → admin inbox ─────────

function SupportCard() {
  const [body, setBody] = useState("");
  const [history, setHistory] = useState<MySupport[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  function load() {
    api.mySupport().then(setHistory).catch((e) => setErr((e as Error).message));
  }
  useEffect(load, []);

  const wc = (body.trim().match(/\S+/g) || []).length;
  const overLimit = wc > SUPPORT_MAX_WORDS;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim() || overLimit) return;
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      await api.submitSupport(body.trim());
      setBody("");
      setOk(true);
      load();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Contact admin">
      <Hint>
        Send the admin team a short note — bug reports, requests,
        questions. Limit {SUPPORT_MAX_WORDS} words.
      </Hint>
      <form onSubmit={submit} className="space-y-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="w-full resize-y rounded-md border border-bg-border bg-bg-card px-3 py-2 text-sm text-white"
          placeholder="What's on your mind?"
        />
        <div className="flex items-center justify-between text-xs">
          <span className={overLimit ? "text-red-400" : "text-ink-dim"}>
            {wc} / {SUPPORT_MAX_WORDS} words
          </span>
          <button
            disabled={busy || overLimit || !body.trim()}
            className="rounded-md border border-white/20 bg-white px-4 py-2 text-sm font-semibold text-bg disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
      {err && <ErrorBox msg={err} />}
      {ok && <SuccessBox msg="Message sent to admins." />}

      {history && history.length > 0 && (
        <div className="mt-4 border-t border-bg-border pt-3">
          <div className="mb-1 text-xs font-medium text-ink-muted">
            Your previous messages
          </div>
          <ul className="space-y-2 text-xs">
            {history.map((m) => (
              <li
                key={m.id}
                className="rounded border border-bg-border bg-bg-card p-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-ink-dim">
                    {fmtDateTime(m.created_at)}
                  </span>
                  <span
                    className={
                      m.status === "resolved"
                        ? "text-emerald-400"
                        : "text-amber-400"
                    }
                  >
                    {m.status}
                  </span>
                </div>
                <div className="mt-1 whitespace-pre-wrap text-ink-muted">
                  {m.body}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ───────── Danger zone (just log out for now) ─────────

function DangerCard() {
  const logout = useAuthStore((s) => s.logout);
  return (
    <Card title="Sign out">
      <button
        onClick={logout}
        className="rounded-md border border-bg-border px-4 py-2 text-sm text-white hover:bg-bg-hover"
      >
        Log out of this device
      </button>
    </Card>
  );
}

// ───────── Helpers ─────────

function PasswordInput({
  value,
  onChange,
  required = false,
  minLength,
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  minLength?: number;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        minLength={minLength}
        className="w-full rounded-md border border-bg-border bg-bg-card px-3 py-2 pr-10 text-sm text-white"
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        tabIndex={-1}
        aria-label={shown ? "Hide password" : "Show password"}
        title={shown ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-dim hover:text-white"
      >
        {shown ? (
          // eye-off
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          // eye
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-bg-border bg-bg-elevated p-5">
      <h2 className="mb-3 text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-bg-border/60 py-1.5 text-sm last:border-b-0">
      <span className="text-ink-muted">{k}</span>
      <span className="text-white">{v}</span>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-dim">
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="mb-3 text-xs text-ink-muted">{children}</div>;
}

function Stat({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: number | string;
  wide?: boolean;
}) {
  return (
    <div
      className={`rounded-md border border-bg-border bg-bg-card px-3 py-2 ${
        wide ? "col-span-2" : ""
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-ink-dim">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
      {msg}
    </div>
  );
}

function SuccessBox({ msg }: { msg: string }) {
  return (
    <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
      {msg}
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString();
}
function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString();
}
function shortUA(ua: string | null) {
  if (!ua) return "—";
  // Strip obvious Mozilla noise — pick the human-readable browser+OS bits.
  const m =
    ua.match(/(Chrome|Firefox|Safari|Edge|OPR)\/[\d.]+/) ||
    ua.match(/[A-Za-z]+\/[\d.]+/);
  const os =
    ua.match(/\(([^)]+)\)/)?.[1]?.split(";")[0]?.trim() ?? "";
  return [m?.[0], os].filter(Boolean).join(" · ").slice(0, 80) || ua.slice(0, 80);
}
